import { useEffect, useRef, useState } from "react";
import { X, Upload, FileText, Loader2, Check, AlertCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Modal de importación masiva de vademecum desde PDF/Imagen/Excel/CSV.
 *
 * Flujo:
 *   1. Usuario elige fichero → POST /vademecum/import-preview → recibe job_id
 *   2. Poll cada 4s a /vademecum/import-jobs/{job_id} hasta status="done"
 *   3. Mostrar preview con bloques de descuento + GHs resueltos + items no reconocidos
 *   4. Usuario revisa/deselecciona entradas → POST /vademecum/import-apply
 *
 * Props:
 *   laboratorio: string
 *   onClose: () => void
 *   onApplied: () => void   // se llama tras aplicar para refrescar el padre
 */
/** Línea no reconocida: permite dar de alta el CN en el catálogo al vuelo. */
function UnmatchedRow({ item, laboratorio, onAdded, added }) {
  const isCn = item.type === "cn";
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const cleanName = String(item.label || "").replace(/\s+EFG\.?$/i, "").trim();
  const [gh, setGh] = useState(
    cleanName.replace(new RegExp(`\\b${String(laboratorio || "").split(/[ ,.]/)[0]}\\b`, "ig"), "").replace(/\s{2,}/g, " ").trim(),
  );
  const [pvl, setPvl] = useState("");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/catalog/quick-add", {
        cn: String(item.value),
        name: cleanName,
        laboratorio,
        gh,
        pvl: pvl === "" ? null : Number(String(pvl).replace(",", ".")),
      });
      toast.success(
        data.created ? "Alta creada en el catálogo" : "Ese código ya estaba en el catálogo",
        data.created
          ? undefined
          : { description: `G.H.: ${data.item?.gh || "sin G.H."}${data.item?.name ? ` · ${data.item.name}` : ""}` },
      );
      onAdded();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo dar de alta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-0.5 border-b border-amber-100 last:border-0">
      <div className="flex items-center gap-2">
        <span className="font-mono-num flex-1 truncate">
          {item.discount_pct}% · {item.type} · {item.value} {item.label ? `· ${item.label}` : ""}
        </span>
        {isCn && !added && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-[10px] uppercase font-bold px-2 py-0.5 rounded border border-amber-400 text-amber-900 hover:bg-amber-100"
            data-testid={`unmatched-add-${item.value}`}
          >
            Dar de alta
          </button>
        )}
        {added && <span className="shrink-0 text-[10px] font-bold text-emerald-700 uppercase">Dado de alta</span>}
      </div>
      {open && (
        <div className="flex flex-wrap items-end gap-2 py-2">
          <label className="text-[10px] uppercase font-bold text-amber-900">
            Grupo homogéneo
            <input
              value={gh}
              onChange={(e) => setGh(e.target.value)}
              className="block w-72 mt-0.5 px-2 py-1 border border-amber-300 rounded text-xs font-normal normal-case"
              data-testid={`unmatched-gh-${item.value}`}
            />
          </label>
          <label className="text-[10px] uppercase font-bold text-amber-900">
            PVL €
            <input
              value={pvl}
              onChange={(e) => setPvl(e.target.value)}
              placeholder="opcional"
              className="block w-24 mt-0.5 px-2 py-1 border border-amber-300 rounded text-xs font-normal"
              data-testid={`unmatched-pvl-${item.value}`}
            />
          </label>
          <button
            onClick={save}
            disabled={saving || !gh.trim()}
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-bold disabled:opacity-50"
            data-testid={`unmatched-save-${item.value}`}
          >
            {saving ? "Guardando…" : "Guardar en catálogo"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function VademecumImportModal({ laboratorio, onClose, onApplied }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [targetLicense, setTargetLicense] = useState("");
  const [addedCns, setAddedCns] = useState([]);
  const [files, setFiles] = useState([]);
  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles((prev) => [...prev, ...incoming.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))].slice(0, 10));
  };
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null); // null | 'running' | 'done' | 'error'
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({}); // gh -> bool
  const [overrides, setOverrides] = useState({}); // gh -> pct
  // Por defecto, las condiciones nuevas SUSTITUYEN a las anteriores de ese
  // laboratorio (decisión del usuario): una tarifa nueva deja sin validez la vieja.
  const [replaceAll, setReplaceAll] = useState(true);
  const [applying, setApplying] = useState(false);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [showDiffDetail, setShowDiffDetail] = useState(false);
  const pollRef = useRef(null);

  const upload = async () => {
    if (!files.length) return;
    setStatus("running");
    setError(null);
    setResult(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const r = await api.post(
        `/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum/import-preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setJobId(r.data.job_id);
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.detail || "No se pudo lanzar el procesamiento");
    }
  };

  // Polling del job
  useEffect(() => {
    if (!jobId || status !== "running") return;
    const tick = async () => {
      try {
        const r = await api.get(
          `/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum/import-jobs/${jobId}`,
        );
        const d = r.data || {};
        if (d.status === "done") {
          setStatus("done");
          setResult(d.result);
          // Por defecto seleccionar todos los resolved
          const sel = {};
          (d.result?.resolved || []).forEach((x) => { sel[x.gh] = true; });
          setSelected(sel);
        } else if (d.status === "error") {
          setStatus("error");
          setError(d.error || "Error procesando documento");
        }
      } catch (e) {
        // silencioso, sigue intentando
      }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => clearInterval(pollRef.current);
  }, [jobId, status, laboratorio]);

  const buildPayload = () => {
    const entries = (result?.resolved || [])
      .filter((x) => selected[x.gh])
      .map((x) => ({
        gh: x.gh,
        discount_pct: overrides[x.gh] != null ? Number(overrides[x.gh]) : Number(x.discount_pct),
      }))
      .filter((x) => x.gh && x.discount_pct > 0 && x.discount_pct <= 100);
    const entries_by_cn = (result?.entries_by_cn || [])
      .map((x) => ({ cn: x.cn, discount_pct: Number(x.discount_pct), label: x.label || "" }))
      .filter((x) => x.cn && x.discount_pct > 0 && x.discount_pct <= 100);
    return {
      entries,
      entries_by_cn,
      noDiscountGhs: result?.no_discount_ghs || [],
      noDiscountCns: result?.no_discount_cns || [],
    };
  };

  // Resumen de impacto: qué CN/G.H. son nuevos y cuáles cambian de descuento
  const loadDiff = async () => {
    if (!result) return;
    setDiffLoading(true);
    try {
      const { entries, entries_by_cn, noDiscountGhs, noDiscountCns } = buildPayload();
      const r = await api.post(
        `/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum/import-diff`,
        { entries, entries_by_cn, no_discount_ghs: noDiscountGhs, no_discount_cns: noDiscountCns, target_license: targetLicense.trim() || null },
      );
      setDiff(r.data);
    } catch (e) {
      setDiff(null);
      toast.error(e?.response?.data?.detail || "No se pudo calcular el resumen");
    } finally {
      setDiffLoading(false);
    }
  };

  useEffect(() => {
    if (status === "done" && result) loadDiff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, result, targetLicense]);

  const applyToVademecum = async () => {
    if (!result) return;
    const { entries, entries_by_cn, noDiscountGhs, noDiscountCns } = buildPayload();
    if (entries.length === 0 && entries_by_cn.length === 0 && noDiscountGhs.length === 0 && noDiscountCns.length === 0) {
      toast.error("No has seleccionado ninguna entrada para aplicar");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post(
        `/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum/import-apply`,
        { entries, entries_by_cn, no_discount_ghs: noDiscountGhs, no_discount_cns: noDiscountCns, replace_all: replaceAll, target_license: targetLicense.trim() || null },
      );
      const appliedGh = r.data.applied || 0;
      const appliedCn = r.data.applied_by_cn || 0;
      const appliedNd = r.data.applied_no_discount || 0;
      const parts = [];
      if (appliedGh) parts.push(`${appliedGh} por G.H.`);
      if (appliedCn) parts.push(`${appliedCn} por CN`);
      if (appliedNd) parts.push(`${appliedNd} sin descuento`);
      toast.success(`${parts.join(" + ")} aplicadas al vademecum${targetLicense.trim() ? ` de ${targetLicense.trim()}` : ""}`);
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar");
    } finally {
      setApplying(false);
    }
  };

  // Agrupar resolved por discount_pct para visualización clara
  const groups = (() => {
    if (!result?.resolved) return [];
    const by = {};
    result.resolved.forEach((x) => { (by[x.discount_pct] = by[x.discount_pct] || []).push(x); });
    return Object.entries(by).sort((a, b) => b[0] - a[0]); // descendente por %
  })();

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose} data-testid="vademecum-import-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Importar vademecum</div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">{laboratorio}</h2>
            <p className="text-xs text-slate-500 mt-1">Sube el PDF, imagen, Excel o CSV con las condiciones del proveedor. La IA detectará bloques (p.ej. &quot;OFERTA 70%&quot;) y grupos moleculares (p.ej. &quot;ATORVASTATINAS&quot;).</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700" data-testid="close-import-modal"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {isAdmin && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="import-target-block">
              <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                Aplicar a otra farmacia (código de licencia)
              </label>
              <input
                type="text"
                value={targetLicense}
                onChange={(e) => setTargetLicense(e.target.value.toUpperCase())}
                placeholder="Ej. PD-2026-0049 — vacío = tu propia cuenta"
                className="w-full sm:max-w-sm px-3 py-2 border border-slate-200 rounded-md text-sm font-mono-num"
                data-testid="import-target-license"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                {targetLicense.trim()
                  ? `Las condiciones de ${laboratorio} se guardarán en la farmacia ${targetLicense.trim()}.`
                  : "Sólo administración: déjalo vacío para aplicarlo a tu propia cuenta."}
              </p>
            </div>
          )}
          {/* Paso 1: subir archivo */}
          {!jobId && (
            <div>
              <label
                className="block border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-sky-500 cursor-pointer transition-colors"
                data-testid="import-file-drop"
              >
                <Upload className="h-8 w-8 mx-auto text-slate-400" />
                <div className="mt-2 text-sm font-semibold text-slate-700">
                  {files.length ? "Añadir más archivos" : "Selecciona uno o varios archivos"}
                </div>
                <div className="text-xs text-slate-500 mt-1">PDF · PNG/JPG · XLSX/XLS · CSV · hasta 10 fotos de la misma tarifa, se revisan juntas</div>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,application/pdf,image/*"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                  data-testid="import-file-input"
                />
              </label>
              {files.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="import-file-list">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${f.size}`} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-3 py-1.5" data-testid={`import-file-item-${i}`}>
                      <span className="truncate text-slate-700">{i + 1}. {f.name}</span>
                      <button
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-rose-600 ml-3"
                        data-testid={`import-file-remove-${i}`}
                        aria-label="Quitar archivo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={upload}
                  disabled={!files.length}
                  className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="import-upload-btn"
                >
                  <Sparkles className="h-4 w-4" /> Procesar con IA{files.length > 1 ? ` (${files.length} archivos)` : ""}
                </button>
              </div>
            </div>
          )}

          {/* Paso 2: procesando */}
          {status === "running" && (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-6 text-center" data-testid="import-running">
              <Loader2 className="h-8 w-8 mx-auto text-sky-600 animate-spin" />
              <div className="mt-3 text-sm font-semibold text-slate-900">Procesando el documento…</div>
              <div className="text-xs text-slate-600 mt-1">Esto puede tardar de 30 segundos a 2 minutos según el tamaño.</div>
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 inline-flex items-start gap-2" data-testid="import-error">
              <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-rose-700 text-sm">No se pudo procesar el documento</div>
                <div className="text-xs text-rose-600 mt-0.5">{error}</div>
              </div>
            </div>
          )}

          {/* Paso 3: preview */}
          {status === "done" && result && (
            <div className="space-y-4">
              {result.warning && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-2" data-testid="import-warning">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">{result.warning}</div>
                </div>
              )}
              {result.cut_rows?.length > 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-4 flex items-start gap-2" data-testid="import-cut-rows-warning">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <div className="font-bold">Hay {result.cut_rows.length === 1 ? "una fila cortada" : `${result.cut_rows.length} filas cortadas`} en la foto: no se ha aplicado su descuento</div>
                    <ul className="mt-1 space-y-0.5">
                      {result.cut_rows.map((c, i) => (
                        <li key={i} data-testid={`import-cut-row-${i}`}>
                          <strong>{c.text}</strong>
                          <span className="text-amber-700"> · {c.position === "bottom" ? "cortada abajo" : c.position === "top" ? "cortada arriba" : "no se lee bien"}{result.files_total > 1 ? ` · ${c.file}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 text-xs">
                      Haz la foto siguiente empezando por {result.cut_rows.length === 1 ? "esa fila" : "esas filas"} y vuelve a importar todas juntas, o añade el descuento a mano abajo.
                    </div>
                  </div>
                </div>
              )}

              {result.files_total > 1 && (
                <div className="text-xs text-slate-600" data-testid="import-files-summary">
                  {result.files_total - (result.files_failed?.length || 0)} de {result.files_total} archivos leídos y revisados juntos.
                  {result.files_failed?.length > 0 && (
                    <span className="text-rose-700 font-semibold"> No se pudieron leer: {result.files_failed.join(", ")}</span>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                  <div className="text-[10px] uppercase font-bold text-emerald-700">G.H. resueltos</div>
                  <div className="text-2xl font-bold text-emerald-900 mt-1 font-mono-num">{result.resolved_count}</div>
                </div>
                <div className="bg-sky-50 border border-sky-200 rounded-md p-3" title="CN sin G.H. resoluble en el catálogo: se aplicarán como descuento por CN directo">
                  <div className="text-[10px] uppercase font-bold text-sky-700">Sólo por CN</div>
                  <div className="text-2xl font-bold text-sky-900 mt-1 font-mono-num">{(result.entries_by_cn || []).filter((e) => !e.gh).length}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <div className="text-[10px] uppercase font-bold text-amber-700">No reconocidos</div>
                  <div className="text-2xl font-bold text-amber-900 mt-1 font-mono-num">{result.unmatched_count}</div>
                </div>
                <div className="bg-slate-100 border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase font-bold text-slate-600">Bloques de descuento</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1 font-mono-num">{(result.blocks_received || []).length}</div>
                </div>
              </div>

              {((result.no_discount_ghs || []).length > 0 || (result.no_discount_cns || []).length > 0) && (
                <div className="bg-rose-50 border border-rose-200 rounded-md p-4" data-testid="import-no-discount">
                  <div className="text-sm font-bold text-rose-800">
                    Sin descuento (0%) · {(result.no_discount_ghs || []).length} moléculas
                    {(result.no_discount_cns || []).length > 0 ? ` · ${(result.no_discount_cns || []).length} CN` : ""}
                  </div>
                  <p className="text-xs text-rose-700 mt-1">
                    Detectadas en el bloque &quot;SIN DESCUENTO / EXCLUIDAS&quot;. Se guardarán como excluidas: nunca se les aplicará el descuento lineal del laboratorio.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {(result.no_discount_ghs || []).slice(0, 120).map((g) => (
                      <span key={g} className="text-[11px] bg-white border border-rose-200 text-rose-700 rounded px-1.5 py-0.5">{g}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Por bloques de % */}
              {groups.map(([pct, items]) => (
                <div key={pct} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`import-block-${pct}`}>
                  <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
                    <div className="font-bold text-slate-900">
                      Descuento {pct}% <span className="text-slate-500 text-xs font-normal">· {items.length} G.H.</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <button onClick={() => {
                        const cp = { ...selected };
                        items.forEach((x) => { cp[x.gh] = true; });
                        setSelected(cp);
                      }} className="text-sky-700 hover:underline">Seleccionar todos</button>
                      <span className="text-slate-300">|</span>
                      <button onClick={() => {
                        const cp = { ...selected };
                        items.forEach((x) => { cp[x.gh] = false; });
                        setSelected(cp);
                      }} className="text-slate-600 hover:underline">Ninguno</button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {items.map((x) => (
                          <tr key={x.gh} className="border-t border-slate-100">
                            <td className="px-3 py-2 w-10">
                              <input
                                type="checkbox"
                                checked={!!selected[x.gh]}
                                onChange={(e) => setSelected({ ...selected, [x.gh]: e.target.checked })}
                                data-testid={`import-check-${x.gh}`}
                              />
                            </td>
                            <td className="px-2 py-2 text-slate-800">{x.gh}</td>
                            <td className="px-3 py-2 w-28 text-right whitespace-nowrap">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                defaultValue={x.discount_pct}
                                onChange={(e) => setOverrides({ ...overrides, [x.gh]: e.target.value })}
                                className="w-20 text-right rounded border-slate-300 px-2 py-1 text-sm font-mono-num"
                                data-testid={`import-pct-${x.gh}`}
                              />
                              <span className="text-slate-500 text-xs ml-0.5">%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {/* Unmatched */}
              {result.unmatched_count > 0 && (
                <details className="border border-amber-200 rounded-md bg-amber-50">
                  <summary className="px-4 py-2 cursor-pointer font-bold text-amber-900 text-sm">
                    Ver {result.unmatched_count} líneas no reconocidas
                  </summary>
                  <div className="max-h-64 overflow-y-auto px-4 pb-3 text-xs text-amber-900">
                    {result.unmatched.slice(0, 200).map((u, i) => (
                      <UnmatchedRow
                        key={i}
                        item={u}
                        laboratorio={laboratorio}
                        onAdded={() => setAddedCns((p) => [...p, String(u.value)])}
                        added={addedCns.includes(String(u.value))}
                      />
                    ))}
                  </div>
                </details>
              )}

              {/* Resumen de impacto antes de aplicar */}
              <div className="border border-slate-200 rounded-lg bg-white" data-testid="import-diff">
                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
                    Qué cambia si aplicas{targetLicense.trim() ? ` en ${targetLicense.trim()}` : ""}
                  </div>
                  <button
                    onClick={loadDiff}
                    disabled={diffLoading}
                    className="text-[11px] font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="import-diff-refresh"
                  >
                    {diffLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Recalcular
                  </button>
                </div>
                {diffLoading && !diff ? (
                  <div className="px-4 pb-3 text-xs text-slate-500">Comparando con lo que ya tenía…</div>
                ) : !diff ? (
                  <div className="px-4 pb-3 text-xs text-slate-500">Pulsa «Recalcular» para ver el impacto.</div>
                ) : (
                  <>
                    <div className="px-4 pb-3 grid grid-cols-3 gap-3" data-testid="import-diff-counts">
                      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3">
                        <div className="text-xl font-bold text-emerald-700 font-mono-num">
                          {diff.gh.new + diff.cn.new}
                        </div>
                        <div className="text-[11px] font-semibold text-emerald-800 uppercase">Nuevas</div>
                        <div className="text-[10px] text-emerald-700">{diff.cn.new} CN · {diff.gh.new} G.H.</div>
                      </div>
                      <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                        <div className="text-xl font-bold text-amber-700 font-mono-num">
                          {diff.gh.changed + diff.cn.changed}
                        </div>
                        <div className="text-[11px] font-semibold text-amber-800 uppercase">Cambian de %</div>
                        <div className="text-[10px] text-amber-700">{diff.cn.changed} CN · {diff.gh.changed} G.H.</div>
                      </div>
                      <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
                        <div className="text-xl font-bold text-slate-700 font-mono-num">
                          {diff.gh.same + diff.cn.same}
                        </div>
                        <div className="text-[11px] font-semibold text-slate-600 uppercase">Sin cambios</div>
                        <div className="text-[10px] text-slate-500">ya tenían este descuento</div>
                      </div>
                    </div>
                    {(diff.gh.changed + diff.cn.changed + diff.gh.new + diff.cn.new) > 0 && (
                      <div className="px-4 pb-3">
                        <button
                          onClick={() => setShowDiffDetail((v) => !v)}
                          className="text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900"
                          data-testid="import-diff-toggle"
                        >
                          {showDiffDetail ? "Ocultar detalle" : "Ver detalle"}
                        </button>
                        {showDiffDetail && (
                          <div className="mt-2 max-h-60 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="import-diff-detail">
                            {[...diff.cn.changed_items, ...diff.gh.changed_items].map((r, i) => (
                              <div key={`c${i}`} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                                <span className="font-mono-num text-slate-500 w-20 flex-shrink-0">{r.cn || ""}</span>
                                <span className="flex-1 truncate text-slate-700">{r.label || r.gh}</span>
                                <span className="font-mono-num text-slate-400 line-through">{r.old_pct}%</span>
                                <span className="font-mono-num font-bold text-amber-700">{r.new_pct}%</span>
                              </div>
                            ))}
                            {[...diff.cn.new_items, ...diff.gh.new_items].map((r, i) => (
                              <div key={`n${i}`} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                                <span className="font-mono-num text-slate-500 w-20 flex-shrink-0">{r.cn || ""}</span>
                                <span className="flex-1 truncate text-slate-700">{r.label || r.gh}</span>
                                <span className="text-[10px] font-bold uppercase text-emerald-700">nueva</span>
                                <span className="font-mono-num font-bold text-emerald-700">{r.new_pct}%</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {isAdmin && targetLicense.trim() && (                <div className="pt-3 border-t border-slate-200 text-xs text-slate-600" data-testid="import-target-recap">
                  Se aplicará a la farmacia <b className="font-mono-num">{targetLicense.trim()}</b>
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pt-3 border-t border-slate-200">
                <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={replaceAll}
                    onChange={(e) => setReplaceAll(e.target.checked)}
                    data-testid="import-replace-all"
                  />
                  <span>
                    Sustituir las condiciones anteriores de este laboratorio
                    <span className="block text-[11px] text-slate-400">
                      Desmárcalo solo si quieres añadir estas condiciones a las que ya tenía
                    </span>
                  </span>
                </label>
                <button
                  onClick={applyToVademecum}
                  disabled={applying}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="import-apply-btn"
                >
                  {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Aplicar al vademecum
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
